import { useFormatDate } from '@ben/common/date';
import InlineTextButton from '@ben/orion/inline-text-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useDisclosure } from '@mantine/hooks';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { MAX_VISIBLE_DOCUMENT_SIGNING_HISTORY_RECORDS } from '../../constants';
import type { DocumentSigningValue } from '../../types/modular-benefit';
import { isEmployeeDocumentSigningCompleted } from '../../utils/document-signing';
import {
  isAwaitingManagerSignature,
  sortDocumentSigningHistory,
} from '../../utils/document-signing-selection';

export interface DocumentSigningSelectionDisplayProps {
  componentName: string;
  signingHistory: DocumentSigningValue[];
}

export const DocumentSigningSelectionDisplay: FC<
  DocumentSigningSelectionDisplayProps
> = ({ componentName, signingHistory }) => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [isExpanded, { toggle: toggleExpanded }] = useDisclosure(false);

  const awaitingManagerSignatureLabel = t(
    'benefitDetails.selections.awaitingManagerSignature'
  );

  const getSignedOnLabel = (
    signedAt: string | null | undefined,
    name: string
  ): string => {
    const dateLabel = signedAt ? formatDate(signedAt) : null;

    if (!dateLabel || dateLabel === '-') {
      return name;
    }

    return t('benefitDetails.selections.signedOn', { date: dateLabel, name });
  };

  const employeeSignedLabel = t('benefitDetails.selections.signedByYou');

  const sortedHistory = sortDocumentSigningHistory(signingHistory);

  const nodes: ReactNode[] = sortedHistory
    .map((signingRequest, index) => {
      if (signingRequest.signedDocumentUrl) {
        return (
          <DocumentSigningLinkRow
            key={`${componentName}-${signingRequest.signingRequestId ?? index}`}
            componentName={componentName}
            documentUrl={signingRequest.signedDocumentUrl}
            signedAt={signingRequest.signedAt}
            getSignedOnLabel={getSignedOnLabel}
          />
        );
      }

      if (isAwaitingManagerSignature(signingRequest)) {
        return (
          <Typography
            key={`${componentName}-awaiting-manager-signature-${index}`}
            variant={TypographyVariant.bodyMd1}
          >
            {awaitingManagerSignatureLabel}
          </Typography>
        );
      }

      if (isEmployeeDocumentSigningCompleted(signingRequest)) {
        return (
          <Typography
            key={`${componentName}-employee-signed-${index}`}
            variant={TypographyVariant.bodyMd1}
          >
            {employeeSignedLabel}
          </Typography>
        );
      }

      return null;
    })
    .filter(Boolean);

  if (nodes.length === 0) {
    return null;
  }

  const shouldShowToggle =
    nodes.length > MAX_VISIBLE_DOCUMENT_SIGNING_HISTORY_RECORDS;
  const visibleNodes = isExpanded
    ? nodes
    : nodes.slice(0, MAX_VISIBLE_DOCUMENT_SIGNING_HISTORY_RECORDS);
  const toggleLabel = isExpanded ? t('words.showLess') : t('words.showMore');

  return (
    <DocumentSigningLinksContainer>
      {visibleNodes}
      {shouldShowToggle && (
        <InlineTextButton
          label={toggleLabel}
          onClick={toggleExpanded}
          isUnderlined
        />
      )}
    </DocumentSigningLinksContainer>
  );
};

interface DocumentSigningLinkRowProps {
  componentName: string;
  documentUrl: string;
  signedAt: string | null | undefined;
  getSignedOnLabel: (
    signedAt: string | null | undefined,
    name: string
  ) => string;
}

const DocumentSigningLinkRow: FC<DocumentSigningLinkRowProps> = ({
  componentName,
  documentUrl,
  signedAt,
  getSignedOnLabel,
}) => (
  <InlineTextButton
    label={getSignedOnLabel(signedAt, componentName)}
    onClick={() => window.open(documentUrl, '_blank', 'noopener,noreferrer')}
    isUnderlined
    isRightIcon
  />
);

const DocumentSigningLinksContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;
